import _initializerDefineProperty from "@babel/runtime-corejs3/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime-corejs3/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime-corejs3/helpers/initializerWarningHelper";
var _class, _descriptor;
import Observer from '@researchgate/react-intersection-observer';
import classNames from 'classnames';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TrackImpression } from '@udemy/event-tracking';
import { useMatchMedia } from '@udemy/hooks';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { IconButton } from '@udemy/react-core-components';
import { Button } from '@udemy/react-core-components';
import { Loader } from '@udemy/react-reveal-components';
import { debounce } from '@udemy/shared-utils';
import { useUDData } from '@udemy/ud-data';
import { ctaButtonUdStyle } from './bar-types';
import { BasicBarContentWithTruncation } from './basic-with-truncation.react-component';
import styles from "./smart-bar-fixed-height.module.css";
import { getSmartBarStore } from './smart-bar-store.mobx-store';

/**
 * Lightweight MobX store for sharing SmartBar height between main bar and spacer
 */
let SmartBarHeightStore = (_class = class SmartBarHeightStore {
  constructor() {
    _initializerDefineProperty(this, "height", _descriptor, this);
  }
  // Start at 0 - only set when data loads AND bar is determined to be sticky

  setHeight(newHeight) {
    this.height = newHeight;
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "height", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "setHeight", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setHeight"), _class.prototype)), _class); // Singleton instance
let smartBarHeightStore = null;

/**
 * Gets or creates the singleton height store instance
 * Exported for testing purposes
 */
export const getSmartBarHeightStore = () => {
  if (!smartBarHeightStore) {
    smartBarHeightStore = new SmartBarHeightStore();
  }
  return smartBarHeightStore;
};

/**
 * Resets the height store singleton (for testing)
 */
export const resetSmartBarHeightStore = () => {
  smartBarHeightStore = null;
};

/**
 * Props for the SmartBarFixedHeight component
 */

/**
 * SmartBarFixedHeight is an experimental variant of SmartBar with a fixed 64px height.
 *
 * Key Differences from Original SmartBar:
 * - Fixed 64px height when collapsed (expands dynamically when "more" is clicked)
 * - Uses BasicBarContentWithTruncation for intelligent text truncation
 * - Implements height tracking with ResizeObserver for accurate height reporting
 * - Responsive button sizing (xsmall on mobile, small on desktop)
 * - Tracks content expansion state to update height
 *
 * This component is designed for A/B testing against the original SmartBar.
 * It maintains API compatibility while introducing the fixed-height behavior.
 */
const SmartBarFixedHeightComponent = props => {
  var _props$disableHideBut, _smartBarStore$notice, _smartBarStore$notice2, _smartBarStore$notice3;
  const [smartBarStore, setSmartBarStore] = useState();
  const [storeCheckComplete, setStoreCheckComplete] = useState(false);
  const {
    me,
    Config,
    request,
    userAgnosticTrackingParams,
    isGlobalMeContextLoading
  } = useUDData();
  const {
    gettext,
    interpolate,
    locale
  } = useI18n();
  const isMobile = useMatchMedia('sm-max');
  const disableHideButtonWhenVisible = (_props$disableHideBut = props.disableHideButtonWhenVisible) !== null && _props$disableHideBut !== void 0 ? _props$disableHideBut : false;
  // Local state for content expansion (triggers ResizeObserver re-measurement)
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  // Ref to the smart bar element for height measurements
  const smartBarRef = useRef(null);

  // Get the shared height store
  const heightStore = getSmartBarHeightStore();

  /**
   * Initialize the smart bar store on mount.
   * Either uses provided store or creates new one if not a spacer.
   *
   * Timing behavior:
   * - If `isUserContextReady` prop is provided: waits for both global me context AND user context
   *   before creating store and making API call. This ensures correct use_case_group is sent.
   * - If `isUserContextReady` is undefined: falls back to legacy behavior (only waits for global me context).
   *
   * The component renders a loading state immediately in both cases, so there's no layout shift.
   */
  useEffect(() => {
    // Determine if we should proceed with store creation
    // If isUserContextReady is explicitly provided, we must wait for it to be true
    // Otherwise, fall back to legacy behavior (only check isGlobalMeContextLoading)
    const isWaitingForUserContext = props.isUserContextReady !== undefined && !props.isUserContextReady;
    if (!isGlobalMeContextLoading && !isWaitingForUserContext && !storeCheckComplete) {
      if (props.store) {
        setSmartBarStore(props.store);
        setStoreCheckComplete(true);
      } else {
        var _window$location;
        const store = getSmartBarStore({
          params: typeof window !== 'undefined' ? Object.fromEntries(new URLSearchParams((_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.search).entries()) : {},
          isPersonalPlanSubscriber: props.isPersonalPlanSubscriber,
          isUdemyBusinessSubscriber: props.isUdemyBusinessSubscriber,
          isEnterprisePlanSubscriber: props.isEnterprisePlanSubscriber,
          isTeamPlanSubscriber: props.isTeamPlanSubscriber,
          skipNoticeBackend: props.skipNoticeBackend,
          udConfig: Config,
          udRequest: request,
          udMe: me,
          userAgnosticTrackingParams,
          gettext
        });
        // Store can be undefined if no notice type is enabled in feature flags
        // Mark check as complete either way to prevent infinite loading
        setSmartBarStore(store);
        setStoreCheckComplete(true);
      }
    }
  }, [isGlobalMeContextLoading, Config, props.isPersonalPlanSubscriber, props.isUdemyBusinessSubscriber, props.isEnterprisePlanSubscriber, props.isTeamPlanSubscriber, props.isUserContextReady, storeCheckComplete, me, request, userAgnosticTrackingParams, props.store, props.skipNoticeBackend, gettext]);

  // Determine if the smart bar should be sticky
  const shouldBeSticky = props.forceNotSticky ? false : smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.isSticky;

  /**
   * Handle expand/collapse toggle from the content component.
   * Uses local state to trigger ResizeObserver re-measurement via dependency array.
   */
  const handleExpandToggle = useCallback(isExpanded => {
    setIsContentExpanded(isExpanded);
  }, []);

  /**
   * ResizeObserver to track height changes in the main SmartBar.
   * Only runs for non-spacer instances and only sets height when bar is sticky.
   * Reports height to store for spacer synchronization.
   * Uses debouncing to batch rapid resize events and prevent layout thrashing.
   * Only updates store when height value actually changes.
   */
  useEffect(() => {
    // Only the main SmartBar (not spacer) should report its height
    if (props.muteEvents || !smartBarRef.current || !smartBarStore) {
      return;
    }

    // Function to measure and update height
    const measureAndUpdateHeight = debounce(() => {
      if (!smartBarRef.current) return;

      // Calculate new height based on state
      const newHeight = smartBarStore.isLoaded && smartBarStore.isSticky ? smartBarRef.current.getBoundingClientRect().height : 0;

      // Only update store if height actually changed
      // This prevents unnecessary MobX reactions that could trigger more resize observations
      if (heightStore.height !== newHeight) {
        heightStore.setHeight(newHeight);
      }
    }, 10);
    const resizeObserver = new ResizeObserver(measureAndUpdateHeight);
    resizeObserver.observe(smartBarRef.current);
    return () => {
      measureAndUpdateHeight.cancel();
      resizeObserver.disconnect();
    };
  }, [props.muteEvents, smartBarStore, isContentExpanded, heightStore]);

  /**
   * Separate effect to measure height when isLoaded, isSticky, or isHidden changes
   * This handles the case where the bar is already sticky when component mounts,
   * and immediately collapses spacer when hide button is clicked
   */
  useEffect(() => {
    if (props.muteEvents || !smartBarStore) {
      return;
    }

    // If hidden, always set height to 0 (even if ref is gone)
    if (smartBarStore.isHidden) {
      heightStore.setHeight(0);
      return;
    }

    // For measuring, we need the ref
    if (!smartBarRef.current) {
      heightStore.setHeight(0);
      return;
    }
    if (smartBarStore.isLoaded && smartBarStore.isSticky) {
      const height = smartBarRef.current.getBoundingClientRect().height;
      heightStore.setHeight(height);
    } else {
      heightStore.setHeight(0);
    }
  }, [smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.isLoaded, smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.isSticky, smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.isHidden, props.muteEvents, heightStore, smartBarStore]);

  // A heading is not required for the SmartBar, so an aria-label is recommended.
  const smartBarAriaLabel = gettext('Marketing notification from Udemy');

  /**
   * Handle opt-in button click.
   * Calls backend API, refreshes page on success.
   */
  const onClickOptIn = useCallback(() => {
    if (!(smartBarStore !== null && smartBarStore !== void 0 && smartBarStore.optInJustApplied())) {
      smartBarStore === null || smartBarStore === void 0 || smartBarStore.optIn().then(() => {
        runInAction(() => {
          smartBarStore === null || smartBarStore === void 0 || smartBarStore.optedIn();
          smartBarStore === null || smartBarStore === void 0 || smartBarStore.sendDealOptInEvent();
          window.location.reload();
        });
      }).catch(smartBarStore.optedOut);
    }
  }, [smartBarStore]);

  /**
   * Handle CTA button click.
   * Sends click event for tracking.
   */
  const onClickCta = useCallback(() => {
    smartBarStore === null || smartBarStore === void 0 || smartBarStore.sendClickEvent(smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.data.get('cta_url'), 'cta');
  }, [smartBarStore]);

  /**
   * Handle link click within content.
   * Sends click event for tracking.
   */
  const handleLinkClick = useCallback((url, location = 'copy_url') => {
    smartBarStore === null || smartBarStore === void 0 || smartBarStore.sendClickEvent(url, location);
  }, [smartBarStore]);

  /**
   * Handle action URL click.
   * Sends generic click event for tracking.
   */
  const handleActionURLClick = useCallback(() => {
    const actionUrl = smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.data.get('action_url');
    if (actionUrl) {
      smartBarStore === null || smartBarStore === void 0 || smartBarStore.sendClickEvent(actionUrl);
    }
  }, [smartBarStore]);

  // FIXED-HEIGHT LOADING STATE - Key difference from original SmartBar
  // Show loading spinner while store initialization is in progress OR data hasn't loaded yet
  // This prevents returning null and causing layout shift
  // Note: storeCheckComplete=false means we haven't tried to get the store yet
  const isStoreLoading = !storeCheckComplete || smartBarStore && !smartBarStore.isLoaded;
  if (isStoreLoading && !props.muteEvents) {
    // Determine if loading state should be sticky
    const shouldLoadingBeSticky = !props.forceNotSticky && (smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.isSticky) && !smartBarStore.isStickyPositionHidden;
    const loadingContainerClasses = classNames(styles['smart-bar-fixed-height'], styles['smart-bar--yellow'], shouldLoadingBeSticky ? styles['smart-bar--sticky'] : null, isMobile ? styles['smart-bar--mobile'] : null);
    const smartBarAriaLabel = gettext('Marketing notification from Udemy');
    return /*#__PURE__*/React.createElement("section", {
      "aria-label": smartBarAriaLabel,
      className: loadingContainerClasses,
      "data-testid": "smart-bar-loading-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['smart-bar__content_wrapper']
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "medium"
    })));
  }

  // Do not render conditions
  // Note: We already handled the loading state above for the main bar (which shows a loader)
  // For spacers (muteEvents=true), return null until data loads to prevent content from appearing during loading
  const shouldNotRender = storeCheckComplete && !smartBarStore ||
  // Store check complete but no notice type enabled in feature flags
  !smartBarStore || smartBarStore.isHidden || request.termsBarType || props.muteEvents && !smartBarStore.isLoaded ||
  // Hide spacer content until notices data loads
  smartBarStore.isStickyPositionHidden && props.hideWhenStickyPositionHidden; // Hide certain SmartBarSpacers when sticky position is hidden

  if (shouldNotRender) {
    return null;
  }

  // Build CSS classes for smart bar
  const smartBarClasses = classNames(styles['smart-bar-fixed-height'], isContentExpanded ? styles['smart-bar-fixed-height--expanded'] : null, shouldBeSticky && !smartBarStore.isStickyPositionHidden ? styles['smart-bar--sticky'] : null, isMobile ? styles['smart-bar--mobile'] : null, (_smartBarStore$notice = smartBarStore.noticeType) === null || _smartBarStore$notice === void 0 ? void 0 : _smartBarStore$notice.classes(smartBarStore.data).split(' ').map(className => styles[className]).join(' '));
  const hideButtonClasses = classNames(styles['smart-bar__close'], smartBarStore.hideButtonHidden ? styles['smart-bar__close_hidden'] : null);
  const contentWrapperClasses = classNames(styles['smart-bar__content_wrapper'], isMobile ? styles['smart-bar__content_wrapper--mobile'] : null);
  const contentClasses = classNames(styles['smart-bar__content'], isMobile ? styles['smart-bar__content--mobile'] : null);
  const ctaButtonWrapperClasses = classNames(styles['smart-bar__cta-button-wrapper'], isMobile ? styles['smart-bar__cta-button-wrapper--mobile'] : null);

  // Extract CTA data from store
  const smartBarCtaText = smartBarStore.data.get('cta_text');
  const smartBarCtaUrl = smartBarStore === null || smartBarStore === void 0 ? void 0 : smartBarStore.data.get('cta_url');
  const smartBarCtaAppliedText = smartBarStore.data.get('cta_applied_text');
  const smartBarCtaTarget = smartBarStore.data.get('cta_target');

  // Build link props for CTA button (open in new tab if configured)
  let linkHtmlProps = {};
  if (smartBarCtaTarget === 'new_tab') {
    linkHtmlProps = {
      target: '_blank',
      rel: 'noopener noreferrer'
    };
  }
  const ctaButtonText = interpolate(gettext('%(ctaText)s'), {
    ctaText: smartBarCtaText
  }, true);
  const ctaAppliedButtonText = interpolate(gettext('%(ctaAppliedText)s'), {
    ctaAppliedText: smartBarCtaAppliedText
  }, true);

  // Check if we have a CTA (used to adjust content truncation)
  const hasCta = Boolean((smartBarStore === null || smartBarStore === void 0 || (_smartBarStore$notice2 = smartBarStore.noticeType) === null || _smartBarStore$notice2 === void 0 ? void 0 : _smartBarStore$notice2.showOptIn) && !smartBarStore.membership.get('opt_in') || smartBarCtaUrl);

  /**
   * Main content component with truncation logic.
   * Prepares data in the format expected by BasicBarContentWithTruncation.
   */
  const content = /*#__PURE__*/React.createElement(BasicBarContentWithTruncation, {
    className: contentClasses,
    data: {
      title: smartBarStore.data.get('title'),
      subtitle: smartBarStore.data.get('subtitle'),
      action_url: smartBarStore.data.get('action_url'),
      enableTimer: smartBarStore.data.get('enable_timer'),
      endTime: smartBarStore.membership.get('end_time'),
      shouldShowSeconds: smartBarStore.data.get('enable_seconds_in_timer'),
      daysToShowTimer: smartBarStore.data.get('days_to_show_timer')
    },
    onLinkClick: handleLinkClick,
    onActionURLClick: handleActionURLClick,
    hasCta: hasCta,
    isMobile: Boolean(isMobile),
    onExpandToggle: handleExpandToggle,
    locale: locale
  });

  /**
   * Opt-in button (shown when user hasn't opted in yet).
   * Shows loading state during API call.
   */
  const optInButton = /*#__PURE__*/React.createElement("div", {
    className: ctaButtonWrapperClasses
  }, /*#__PURE__*/React.createElement(Button, {
    className: "smart-bar__cta-button",
    "data-testid": "smart-bar-opt-in-cta",
    udStyle: ctaButtonUdStyle(smartBarStore.data),
    size: isMobile ? 'xsmall' : 'small',
    onClick: onClickOptIn
  }, smartBarStore.optInJustApplied() ? ctaAppliedButtonText : smartBarStore.optInApplying() ? /*#__PURE__*/React.createElement(Loader, {
    color: "inherit",
    overlay: true
  }) : ctaButtonText));

  /**
   * Link button (shown when CTA URL is provided).
   * Opens link in same or new tab based on configuration.
   */
  const linkButton = /*#__PURE__*/React.createElement("div", {
    className: ctaButtonWrapperClasses
  }, /*#__PURE__*/React.createElement(Button, Object.assign({
    className: "smart-bar__cta-button",
    "data-testid": "smart-bar-link-cta",
    udStyle: ctaButtonUdStyle(smartBarStore.data),
    size: isMobile ? 'xsmall' : 'small',
    componentClass: "a",
    href: smartBarCtaUrl,
    onClick: onClickCta
  }, linkHtmlProps), ctaButtonText));

  // Determine which CTA button to show (if any)
  let ctaButton = null;
  if (smartBarStore !== null && smartBarStore !== void 0 && (_smartBarStore$notice3 = smartBarStore.noticeType) !== null && _smartBarStore$notice3 !== void 0 && _smartBarStore$notice3.showOptIn && !smartBarStore.membership.get('opt_in')) {
    ctaButton = optInButton;
  } else if (smartBarCtaUrl) {
    ctaButton = linkButton;
  }

  /**
   * Hide/close button.
   * Visibility controlled by store logic (sticky position, opt-in state, etc.)
   * Size varies: xsmall icon (16x16px) on mobile, medium icon (24x24px) on desktop
   */
  const closeButtonSize = isMobile ? 'small' : 'medium';
  const closeIconSize = isMobile ? 'xsmall' : 'small';
  const hideButton = /*#__PURE__*/React.createElement("div", {
    className: styles['smart-bar__close_wrapper']
  }, smartBarStore.shouldRenderHideButton && /*#__PURE__*/React.createElement(IconButton, {
    udStyle: "ghost",
    className: hideButtonClasses,
    size: closeButtonSize,
    onClick: () => smartBarStore.hide(),
    "data-testid": "smart-bar-hide"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "close",
    label: gettext('Close'),
    size: closeIconSize
  })));

  /**
   * Layout structure (matches v2):
   * - Content + CTA are always together inside content_wrapper
   * - Hide button is always outside content_wrapper
   * - Mobile vs desktop styling is handled via CSS classes
   * - Spacer wrapper div (not this component) gets inline height from store to match main SmartBar
   */
  let smartBar = /*#__PURE__*/React.createElement("div", {
    ref: smartBarRef,
    className: smartBarClasses,
    "data-testid": "smart-bar-fixed-height"
  }, /*#__PURE__*/React.createElement("div", {
    className: contentWrapperClasses
  }, content, ctaButton), hideButton);

  /**
   * Wrap with IntersectionObserver if tracking top-of-page visibility.
   * Used to toggle hide button visibility based on scroll position.
   */
  if (disableHideButtonWhenVisible) {
    const options = {
      onChange: ioEntry => {
        smartBarStore.setIsInStickyPosition(!ioEntry.isIntersecting);
      }
    };
    smartBar = /*#__PURE__*/React.createElement(Observer, options, smartBar);
  }

  /**
   * Wrap with impression tracking (unless muted for spacer).
   * Sends impression event when smart bar first becomes visible.
   */
  if (props.muteEvents) {
    return /*#__PURE__*/React.createElement("section", {
      "aria-label": smartBarAriaLabel,
      "data-testid": "smart-bar-fixed-height-container"
    }, smartBar);
  }
  return /*#__PURE__*/React.createElement(TrackImpression, {
    trackFunc: () => {
      smartBarStore.sendImpressionsEvent();
    }
  }, /*#__PURE__*/React.createElement("section", {
    "aria-label": smartBarAriaLabel,
    "data-testid": "smart-bar-fixed-height-container"
  }, smartBar));
};
SmartBarFixedHeightComponent.displayName = 'SmartBarFixedHeight';
export const SmartBarFixedHeight = observer(SmartBarFixedHeightComponent);

/**
 * SmartBarFixedHeightSpacer wraps a SmartBarFixedHeight with visibility: hidden.
 *
 * It is used to keep the layout consistent with a SmartBar in sticky position
 * for page elements that depend on the height of the SmartBar, such as the
 * Header contents and the CLP slider-menu.
 *
 * The spacer only renders when heightStore.height > 0, which only happens when:
 * 1. Data has loaded (isLoaded = true)
 * 2. AND the bar is determined to be sticky (isSticky = true)
 *
 * This prevents the spacer from appearing during loading or for non-sticky bars.
 *
 * @param {boolean} disableHideButtonWhenVisible - True for SmartBarSpacer intended to track when top of page is visible
 */

const SmartBarFixedHeightSpacerComponent = ({
  disableHideButtonWhenVisible = false,
  ...rest
}) => {
  // Get the shared height store (singleton)
  const heightStore = getSmartBarHeightStore();

  // Only render spacer wrapper if height > 0
  // Height is only set when data loads AND bar is determined to be sticky
  // This prevents spacer from appearing during loading or for non-sticky bars
  if (heightStore.height === 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classNames('smart-bar-spacer', styles['smart-bar-spacer']),
    "data-testid": "smart-bar-fixed-height-spacer",
    style: {
      height: `${heightStore.height}px`
    }
  }, /*#__PURE__*/React.createElement(SmartBarFixedHeight, Object.assign({
    forceNotSticky: true,
    skipNoticeBackend: true,
    muteEvents: true,
    hideWhenStickyPositionHidden: true,
    disableHideButtonWhenVisible: disableHideButtonWhenVisible
  }, rest)));
};
SmartBarFixedHeightSpacerComponent.displayName = 'SmartBarFixedHeightSpacer';
export const SmartBarFixedHeightSpacer = observer(SmartBarFixedHeightSpacerComponent);
//# sourceMappingURL=smart-bar-fixed-height.react-component.js.map